import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import {
  useGetAdvisorSessionsQuery,
  useLazyGetAdvisorSessionsQuery
} from "../../redux/queries"
import { dateRangeToTimestamps } from "../utils/datetime"
import { AdvisorSession } from "../../types"
import { useSubscriptionMap } from "./use-subscription-map"
import { useUserLastActivity } from "./use-user-last-activity"
import { useDateFilter } from "./use-date-filter"

const LIMIT = 20
const IDLE_THRESHOLD = 180000 // 3 minutes
const POLL_INTERVAL = 10000 // 10 seconds

export const useAdvisorConversations = () => {
  const [fetchPage, { isFetching }] = useLazyGetAdvisorSessionsQuery()
  const lastActivity = useUserLastActivity()

  const [allSessions, setAllSessions] = useState<AdvisorSession[]>([])
  const [page, setPage] = useState(1)
  const [hasMore, setHasMore] = useState(false)
  const [isLoading, setIsLoading] = useState(true)

  const isMounted = useRef(false)
  const subscriptionMap = useSubscriptionMap(allSessions)

  const [searchQuery, setSearchQuery] = useState("")
  const isSearchActive = searchQuery.trim().length > 0

  // Polling query — fetches page 1 every 10s, skips when idle or searching
  const { data: pollData } = useGetAdvisorSessionsQuery(
    { page: 1, limit: LIMIT },
    {
      pollingInterval: POLL_INTERVAL,
      skip: isSearchActive || Date.now() - lastActivity > IDLE_THRESHOLD
    }
  )

  // Merge polled results into allSessions
  useEffect(() => {
    if (!pollData?.conversations?.length) return

    setAllSessions(prev => {
      if (prev.length === 0) return prev // initial load not done yet

      const existingIds = new Set(prev.map(s => s._id))
      const updatedMap = new Map(pollData.conversations.map(s => [s._id, s]))

      // Update existing sessions in place
      const merged = prev.map(s => updatedMap.get(s._id) ?? s)

      // Prepend new sessions not already in the list
      const newSessions = pollData.conversations.filter(
        s => !existingIds.has(s._id)
      )

      return newSessions.length > 0 ? [...newSessions, ...merged] : merged
    })
  }, [pollData])

  const fetchAndAppend = useCallback(
    async (pageNum: number, reset = false) => {
      try {
        const result = await fetchPage({ page: pageNum, limit: LIMIT }).unwrap()
        const incoming = result.conversations ?? []
        setAllSessions(prev => (reset ? incoming : [...prev, ...incoming]))
        setHasMore(pageNum < result.totalPages)
        setPage(pageNum + 1)
      } catch {
        // handled by RTK Query
      } finally {
        setIsLoading(false)
      }
    },
    [fetchPage]
  )

  useEffect(() => {
    setIsLoading(true)
    fetchAndAppend(1, true)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const loadMore = useCallback(() => {
    if (!hasMore || isFetching) return
    fetchAndAppend(page)
  }, [hasMore, isFetching, page, fetchAndAppend])

  const [selectedBrandId, setSelectedBrandId] = useState("")
  const {
    datePreset,
    customDateStart,
    customDateEnd,
    handleDateChange,
    handleClearDate
  } = useDateFilter()

  // Skip brand filter on initial mount; reset + refetch on subsequent changes
  useEffect(() => {
    if (!isMounted.current) {
      isMounted.current = true
      return
    }
    setIsLoading(true)
    setAllSessions([])
    setPage(1)
    setHasMore(false)
    fetchAndAppend(1, true)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedBrandId])

  const sessions = useMemo(() => {
    const q = searchQuery.trim().toLowerCase()
    const { min, max } = dateRangeToTimestamps(
      datePreset,
      customDateStart ? new Date(customDateStart) : null,
      customDateEnd ? new Date(customDateEnd) : null,
      { unit: "milliseconds" }
    )

    return allSessions.filter(s => {
      if (q) {
        const sub = s.subscriptionId
          ? subscriptionMap[s.subscriptionId]
          : undefined
        if (!sub) return false
        const fullName = `${sub.first_name ?? ""} ${
          sub.last_name ?? ""
        }`.toLowerCase()
        if (
          !fullName.includes(q) &&
          !(sub.email ?? "").toLowerCase().includes(q)
        )
          return false
      }

      if (selectedBrandId && s.brandId !== selectedBrandId) return false

      if (min || max) {
        const t = new Date(s.lastActivityAt).getTime()
        if (min && t < min) return false
        if (max && t > max) return false
      }

      return true
    })
  }, [
    allSessions,
    searchQuery,
    selectedBrandId,
    datePreset,
    customDateStart,
    customDateEnd,
    subscriptionMap
  ])

  return {
    sessions,
    isLoading,
    hasMore,
    isFetchingMore: isFetching,
    loadMore,
    searchQuery,
    setSearchQuery,
    selectedBrandId,
    setSelectedBrandId,
    datePreset,
    customDateStart,
    customDateEnd,
    handleDateChange,
    handleClearDate,
    subscriptionMap
  }
}
